import { Box, Divider, Flex, HStack, Heading, Text } from '@chakra-ui/react';

import { MarketplaceVehicle, formatVehicleTitle } from '../../../../../utils/cars';
import { PayoffDisplay } from '../hooks/usePayoffDisplay';
import { ListingStatus } from '../listingStatus';
import PayoffStatSlot from './PayoffStatSlot';
import { calmPulseAnimation } from './animations';

export type HeaderIndicator = ListingStatus | 'parkedBuyout' | 'parkedFinishLease';

type MarketplaceLiveHeaderProps = {
  vehicle: MarketplaceVehicle;
  vin?: string | null;
  indicator: HeaderIndicator;
  payoffDisplay?: PayoffDisplay;
};

type IndicatorStyle = {
  dotColor: string;
  textColor: string;
  desktopLabel: string;
  mobileLabel: string;
};

const INDICATOR_STYLES: Partial<Record<HeaderIndicator, IndicatorStyle>> = {
  active: {
    dotColor: 'leaseEndGreen',
    textColor: 'greenLight',
    desktopLabel: 'Your listing is live',
    mobileLabel: 'Your listing is live',
  },
  almostLive: {
    dotColor: 'leaseEndYellow',
    textColor: 'leaseEndYellow',
    desktopLabel: 'Almost live · confirming your payoff',
    mobileLabel: 'Almost live',
  },
  parkedBuyout: {
    dotColor: 'leaseEndYellow',
    textColor: 'leaseEndYellow',
    desktopLabel: 'Buyout path · listing on hold',
    mobileLabel: 'Listing on hold',
  },
  parkedFinishLease: {
    dotColor: 'leaseEndYellow',
    textColor: 'leaseEndYellow',
    desktopLabel: 'Finishing your lease · listing on hold',
    mobileLabel: 'Listing on hold',
  },
};

const MarketplaceLiveHeader = ({
  vehicle,
  vin,
  indicator,
  payoffDisplay,
}: MarketplaceLiveHeaderProps) => {
  const indicatorStyle = INDICATOR_STYLES[indicator];
  const showPayoffSlot = payoffDisplay != null && payoffDisplay.mode !== 'none';

  return (
    <Box
      as="section"
      aria-label="Listing status"
      bgGradient="linear(135deg, leaseEndBlue, leaseEndBlueDark)"
      borderRadius="14px"
      px={{ base: '16px', md: '24px' }}
      py={{ base: '16px', md: '18px' }}
    >
      <Flex
        direction={{ base: 'column', md: 'row' }}
        align={{ base: 'flex-start', md: 'center' }}
        gap={{ base: '4px', md: '12px' }}
      >
        <Box flex="1" minW={0}>
          {indicatorStyle != null && (
            <HStack spacing={2} mb={1.5}>
              <Box
                w="7px"
                h="7px"
                borderRadius="full"
                bg={indicatorStyle.dotColor}
                flexShrink={0}
                animation={calmPulseAnimation}
              />
              <Text
                fontSize="11px"
                fontWeight="700"
                letterSpacing="0.08em"
                textTransform="uppercase"
                color={indicatorStyle.textColor}
                lineHeight="1"
              >
                <Box as="span" display={{ base: 'none', md: 'inline' }}>
                  {indicatorStyle.desktopLabel}
                </Box>
                <Box as="span" display={{ base: 'inline', md: 'none' }}>
                  {indicatorStyle.mobileLabel}
                </Box>
              </Text>
            </HStack>
          )}
          <Heading
            as="h1"
            fontSize={{ base: '22px', md: '28px' }}
            fontWeight="600"
            color="white"
            lineHeight="1.1"
            letterSpacing="-0.01em"
          >
            {formatVehicleTitle(vehicle)}
          </Heading>
          {vin != null && vin !== '' && (
            <Text fontSize="12px" color="whiteAlpha.800" letterSpacing="0.03em" mt="3px">
              <Box
                as="span"
                fontWeight="700"
                fontSize="10px"
                letterSpacing="0.12em"
                textTransform="uppercase"
                color="whiteAlpha.500"
                mr="6px"
              >
                VIN
              </Box>
              {vin}
            </Text>
          )}
        </Box>
        {showPayoffSlot && (
          <Box flexShrink={0} display={{ base: 'none', md: 'block' }}>
            <PayoffStatSlot display={payoffDisplay} align="right" />
          </Box>
        )}
      </Flex>
      {showPayoffSlot && (
        <Box display={{ base: 'block', md: 'none' }} mt="12px">
          <Divider borderColor="whiteAlpha.300" mb="10px" />
          <PayoffStatSlot display={payoffDisplay} align="left" />
        </Box>
      )}
    </Box>
  );
};

export default MarketplaceLiveHeader;
